import { Component, Inject, OnInit, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatButtonModule } from '@angular/material/button';
import { MatDialogModule, MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatTableModule } from '@angular/material/table';
import { DashboardService } from '@services/dashboard.service';
import { DashboardMeasureToolPatientResult, DashboardMeasureToolMetaDataResult } from '@models/measure-tool.model';
import { forkJoin, take, timeout, catchError, of, tap } from 'rxjs';

export interface MeasureLogicReportData {
  organizationId: number;
  patientID: number;
  patientMRN: string;
  measureCode: string;
}

interface CodeInfo {
  code: string;
  shortname: string;
  oid: string;
}

interface EvidenceGroup {
  description: string;
  codes: CodeInfo[];
  isHit: boolean;
}

interface ComponentGroup {
  logic: string;
  evidenceGroups: { [key: string]: EvidenceGroup };
  isHit: boolean;
}

@Component({
  selector: 'app-measure-logic-report-modal',
  standalone: true,
  imports: [
    CommonModule,
    MatButtonModule,
    MatDialogModule,
    MatIconModule,
    MatProgressSpinnerModule,
    MatExpansionModule,
    MatTableModule
  ],
  templateUrl: './measure-logic-report-modal.component.html',
  styleUrls: ['./measure-logic-report-modal.component.css']
})
export class MeasureLogicReportModalComponent implements OnInit {
  private readonly dashboardService = inject(DashboardService);

  isLoading = true;
  errorMessage = '';
  metaData: DashboardMeasureToolMetaDataResult[] = [];
  patientData: DashboardMeasureToolPatientResult[] = [];
  componentGroups: { [key: string]: ComponentGroup } = {};
  componentArray: [string, ComponentGroup][] = [];
  expandedComponents = new Set<string>();
  expandedGroups = new Set<string>();
  displayedColumns: string[] = ['code', 'type', 'oid'];

  constructor(
    public dialogRef: MatDialogRef<MeasureLogicReportModalComponent>,
    @Inject(MAT_DIALOG_DATA) public data: MeasureLogicReportData
  ) {}

  ngOnInit(): void {
    console.log('MeasureLogicReportModal - ngOnInit with data:', this.data);
    this.loadData();
  }

  loadData(): void {
    this.errorMessage = '';
    this.isLoading = true;

    console.log('Starting API calls...');
    console.log('getMeasureToolMetaData params:', this.data.measureCode);
    console.log('getMeasureToolPatientData params:', {
      organizationId: this.data.organizationId,
      patientID: this.data.patientID.toString(),
      measureCode: this.data.measureCode
    });

    // Load both APIs sequentially to avoid completion issues
    this.dashboardService.getMeasureToolMetaData(this.data.measureCode)
      .pipe(take(1))
      .subscribe({
        next: (metaDataResult) => {
          console.log('MetaData API returned:', metaDataResult);
          this.metaData = metaDataResult;

          // Now load patient data
          this.dashboardService.getMeasureToolPatientData(
            this.data.organizationId,
            this.data.patientID.toString(),
            this.data.measureCode
          )
            .pipe(take(1))
            .subscribe({
              next: (patientDataResult) => {
                console.log('PatientData API returned:', patientDataResult);
                this.patientData = patientDataResult;

                console.log('Both APIs completed, processing data...');
                this.processData();
                this.isLoading = false;

                console.log('Processing complete. ComponentArray:', this.componentArray);
              },
              error: (error) => {
                console.error('PatientData API error:', error);
                this.errorMessage = 'Failed to load patient data. Please try again.';
                this.isLoading = false;
              }
            });
        },
        error: (error) => {
          console.error('MetaData API error:', error);
          this.errorMessage = 'Failed to load measure metadata. Please try again.';
          this.isLoading = false;
        }
      });
  }

  private processData(): void {
    this.componentGroups = {};

    // Group metadata by component
    this.metaData.forEach(row => {
      const componentCode = row.componentCode || 'Unknown';
      const logic = row.componentLogic || 'Unknown';

      if (!this.componentGroups[componentCode]) {
        this.componentGroups[componentCode] = {
          logic: logic,
          evidenceGroups: {},
          isHit: this.doesPatientHitComponent(componentCode)
        };
      }

      const egCode = row.evidenceGroupCode || 'Unknown';
      if (!this.componentGroups[componentCode].evidenceGroups[egCode]) {
        this.componentGroups[componentCode].evidenceGroups[egCode] = {
          description: row.evidenceDescription || '',
          codes: [],
          isHit: this.doesPatientHitEvidenceGroup(componentCode, egCode)
        };
      }

      this.componentGroups[componentCode].evidenceGroups[egCode].codes.push({
        code: row.code,
        shortname: row.shortName,
        oid: row.qdmElementOID
      });
    });

    this.componentArray = Object.entries(this.componentGroups);
  }

  private doesPatientHitComponent(componentCode: string): boolean {
    if (this.patientData.length === 0) return false;
    return this.patientData.some(r =>
      r.componentCode === componentCode &&
      r.hitMeasureResults !== null &&
      r.hitMeasureResults !== undefined &&
      r.hitMeasureResults !== ''
    );
  }

  private doesPatientHitEvidenceGroup(componentCode: string, evidenceGroupCode: string): boolean {
    if (this.patientData.length === 0) return false;
    return this.patientData.some(r =>
      r.componentCode === componentCode &&
      r.evidenceGroupCode === evidenceGroupCode &&
      r.hitMeasureResults !== null &&
      r.hitMeasureResults !== undefined &&
      r.hitMeasureResults !== ''
    );
  }

  getComponentName(code: string): string {
    const names: { [key: string]: string } = {
      'IPP': 'Initial Patient Population (IPP)',
      'Num': 'Numerator (Num)',
      'DenExcp': 'Denominator Exception (DenExcp)',
      'Den': 'Denominator (Den)',
      'NumExcp': 'Numerator Exception (NumExcp)'
    };
    return names[code] || code;
  }

  toggleComponent(componentCode: string): void {
    if (this.expandedComponents.has(componentCode)) {
      this.expandedComponents.delete(componentCode);
    } else {
      this.expandedComponents.add(componentCode);
    }
  }

  isComponentExpanded(componentCode: string): boolean {
    return this.expandedComponents.has(componentCode);
  }

  toggleGroup(componentCode: string, groupCode: string): void {
    const key = `${componentCode}-${groupCode}`;
    if (this.expandedGroups.has(key)) {
      this.expandedGroups.delete(key);
    } else {
      this.expandedGroups.add(key);
    }
  }

  isGroupExpanded(componentCode: string, groupCode: string): boolean {
    const key = `${componentCode}-${groupCode}`;
    return this.expandedGroups.has(key);
  }

  getGroupArray(component: ComponentGroup): [string, EvidenceGroup][] {
    return Object.entries(component.evidenceGroups);
  }

  getTotalCodesInComponent(component: ComponentGroup): number {
    return Object.values(component.evidenceGroups).reduce((sum, g) => sum + g.codes.length, 0);
  }

  onClose(): void {
    this.dialogRef.close();
  }
}
